import WhatsAppConnect from '../../components/WhatsAppConnect';
import DashboardLayout from '../../components/DashboardLayout';
import React, { useState, useEffect } from 'react';
import { MessageSquare, CheckCircle, ExternalLink, Copy, ChevronRight, AlertCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function WhatsAppSetup() {
  const [currentStep, setCurrentStep] = useState(1);
  const [config, setConfig] = useState({
    phone_number_id: '',
    access_token: '',
    business_account_id: '',
    display_phone_number: ''
  });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [existingConfig, setExistingConfig] = useState(null);

  useEffect(() => {
    fetchExistingConfig();
  }, []);

  const fetchExistingConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const clinicId = localStorage.getItem('clinicId');
      const response = await fetch(`${API}/api/clinic/${clinicId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.whatsapp_config) {
          setExistingConfig(data.whatsapp_config);
          setConfig({
            phone_number_id: data.whatsapp_config.phone_number_id || '',
            access_token: (data.whatsapp_config.access_token || data.whatsapp_config.access_token_set) ? '••••••••••••••••' : '',
            business_account_id: data.whatsapp_config.business_account_id || '',
            display_phone_number: data.whatsapp_config.display_phone_number || ''
          });
        }
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copiado al portapapeles');
  };

  const webhookUrl = `https://aimedplanner.com/api/webhook/whatsapp`;

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const token = localStorage.getItem('token');
      const clinicId = localStorage.getItem('clinicId');
      
      const response = await fetch(`${API}/api/clinic/${clinicId}/whatsapp/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      setTestResult(result);
    } catch (error) {
      setTestResult({ success: false, error: error.message });
    } finally {
      setTesting(false);
    }
  };

  const saveConfig = async () => {
    if (!config.phone_number_id || !config.access_token || config.access_token === '••••••••••••••••') {
      alert('Completa el Phone Number ID y Access Token');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const clinicId = localStorage.getItem('clinicId');
      
      const response = await fetch(`${API}/api/clinic/${clinicId}/whatsapp/configure`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phone_number_id: config.phone_number_id,
          access_token: config.access_token,
          business_account_id: config.business_account_id,
          display_phone_number: config.display_phone_number
        })
      });

      if (response.ok) {
        alert('✅ Configuración guardada exitosamente');
        setCurrentStep(4);
        fetchExistingConfig();
      } else {
        const error = await response.json();
        alert(`❌ Error: ${error.detail || 'Error guardando configuración'}`);
      }
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const steps = [
    { number: 1, title: 'Crear App en Meta' },
    { number: 2, title: 'Obtener Credenciales' },
    { number: 3, title: 'Configurar Webhook' },
    { number: 4, title: 'Probar Conexión' }
  ];

  return (
    <DashboardLayout title="Conectar WhatsApp">
    <div className="p-6 max-w-4xl mx-auto" data-testid="whatsapp-setup-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-400">
          <MessageSquare className="w-7 h-7 inline mr-2 text-green-500" />
          Conectar WhatsApp
        </h1>
        <p className="text-gray-600 mt-1">Vincula el WhatsApp de esta clínica y comprueba la conexión.</p>
      </div>

      <section className="mb-8 rounded-xl border border-gray-600 bg-slate-900 p-6" aria-label="Conexión oficial con Meta">
        <h2 className="mb-3 text-lg font-semibold text-white">Conectar con Meta</h2>
        <WhatsAppConnect />
      </section>
      <details className="rounded-xl border border-gray-600 p-4">
        <summary className="cursor-pointer text-sm text-gray-400">Configuración manual avanzada</summary>
      {/* Status Banner */}
      {existingConfig?.phone_number_id && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center">
          <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
          <div>
            <p className="font-medium text-green-800">Configuración manual guardada</p>
            <p className="text-sm text-green-600">
              Phone ID: {existingConfig.phone_number_id} 
              {existingConfig.display_phone_number && ` • ${existingConfig.display_phone_number}`}
            </p>
          </div>
        </div>
      )}

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <React.Fragment key={step.number}>
              <div
                className={`flex items-center cursor-pointer ${currentStep >= step.number ? 'text-green-600' : 'text-gray-400'}`}
                onClick={() => setCurrentStep(step.number)}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  currentStep > step.number ? 'bg-green-500 text-white' :
                  currentStep === step.number ? 'bg-green-100 text-green-600 border-2 border-green-500' :
                  'bg-gray-100 text-gray-400'
                }`}>
                  {currentStep > step.number ? <CheckCircle className="w-5 h-5" /> : step.number}
                </div>
                <span className="ml-2 text-sm font-medium hidden sm:block">{step.title}</span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-1 mx-4 ${currentStep > step.number ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        {currentStep === 1 && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">Paso 1: Crear App en Meta for Developers</h2>
            
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="font-medium text-blue-800 mb-2">1. Ve a Meta for Developers</p>
                <a
                  href="https://developers.facebook.com/apps"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-blue-600 hover:underline"
                >
                  developers.facebook.com/apps
                  <ExternalLink className="w-4 h-4 ml-1" />
                </a>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-800 mb-2">2. Crea una nueva app</p>
                <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                  <li>Haz clic en "Crear app"</li>
                  <li>Selecciona "Empresa" como tipo de app</li>
                  <li>Nombre: "AI Med Planner" (o el nombre de tu clínica)</li>
                  <li>Selecciona tu cuenta de Business</li>
                </ul>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-800 mb-2">3. Agrega WhatsApp a tu app</p>
                <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                  <li>En el dashboard de la app, busca "WhatsApp"</li>
                  <li>Haz clic en "Configurar"</li>
                  <li>Sigue el proceso de verificación del negocio si es necesario</li>
                </ul>
              </div>
            </div>

            <button
              onClick={() => setCurrentStep(2)}
              className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium flex items-center justify-center"
            >
              Continuar
              <ChevronRight className="w-5 h-5 ml-2" />
            </button>
          </div>
        )}

        {currentStep === 2 && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">Paso 2: Obtener Credenciales</h2>
            
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Encuentra estos valores en tu app de Meta → WhatsApp → Configuración de la API
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number ID *
                </label>
                <input
                  type="text"
                  value={config.phone_number_id}
                  onChange={(e) => setConfig({...config, phone_number_id: e.target.value})}
                  placeholder="Ej: 123456789012345"
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 bg-white text-gray-900"
                  data-testid="phone-number-id-input"
                />
                <p className="text-xs text-gray-500 mt-1">Lo encuentras en WhatsApp → Configuración de la API → Phone Number ID</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Access Token (Permanente) *
                </label>
                <input
                  type="password"
                  value={config.access_token}
                  onChange={(e) => setConfig({...config, access_token: e.target.value})}
                  placeholder="Ej: EAAG..."
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 bg-white text-gray-900"
                  data-testid="access-token-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Ve a Configuración del negocio → Usuarios del sistema → Generar token permanente
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Account ID (opcional)
                </label>
                <input
                  type="text"
                  value={config.business_account_id}
                  onChange={(e) => setConfig({...config, business_account_id: e.target.value})}
                  placeholder="Ej: 123456789012345"
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 bg-white text-gray-900"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Número de teléfono (para mostrar)
                </label>
                <input
                  type="text"
                  value={config.display_phone_number}
                  onChange={(e) => setConfig({...config, display_phone_number: e.target.value})}
                  placeholder="Ej: +52 55 1234 5678"
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 bg-white text-gray-900"
                />
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => setCurrentStep(1)}
                className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium"
              >
                Atrás
              </button>
              <button
                onClick={() => setCurrentStep(3)}
                disabled={!config.phone_number_id || !config.access_token || config.access_token === '••••••••••••••••'}
                className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center justify-center"
              >
                Continuar
                <ChevronRight className="w-5 h-5 ml-2" />
              </button>
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">Paso 3: Configurar Webhook</h2>
            
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800 mb-3">
                Configura el webhook en Meta para recibir mensajes entrantes
              </p>
              <p className="font-medium text-blue-800">
                Ve a: WhatsApp → Configuración → Webhooks → Editar
              </p>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">URL del Webhook</label>
                <div className="flex items-center">
                  <code className="flex-1 px-4 py-3 bg-white border rounded-l-lg text-sm overflow-x-auto">
                    {webhookUrl}
                  </code>
                  <button
                    onClick={() => copyToClipboard(webhookUrl)}
                    className="px-4 py-3 bg-gray-100 border border-l-0 rounded-r-lg hover:bg-gray-200"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">Token de Verificación</label>
                <div className="flex items-center">
                  <code className="flex-1 px-4 py-3 bg-white border rounded-l-lg text-sm">
                    aimedplanner_verify_token
                  </code>
                  <button
                    onClick={() => copyToClipboard('aimedplanner_verify_token')}
                    className="px-4 py-3 bg-gray-100 border border-l-0 rounded-r-lg hover:bg-gray-200"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-800 mb-2">Campos del Webhook a suscribir:</p>
                <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                  <li><code>messages</code> - Para recibir mensajes</li>
                  <li><code>message_deliveries</code> - Confirmación de entrega</li>
                  <li><code>message_reads</code> - Confirmación de lectura</li>
                </ul>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => setCurrentStep(2)}
                className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium"
              >
                Atrás
              </button>
              <button
                onClick={saveConfig}
                disabled={saving}
                className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center justify-center"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    Guardar y Continuar
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {currentStep === 4 && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">Paso 4: Probar Conexión</h2>
            
            <div className="p-6 bg-green-50 rounded-xl text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-green-800 mb-2">¡Configuración Completa!</h3>
              <p className="text-green-600">Tu WhatsApp Business está configurado correctamente</p>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="font-medium text-gray-800 mb-3">Prueba la conexión:</p>
              <ol className="list-decimal list-inside text-gray-600 space-y-2 text-sm">
                <li>Envía un mensaje de WhatsApp al número que configuraste</li>
                <li>El mensaje debería aparecer en tu dashboard de Conversaciones</li>
                <li>La IA responderá automáticamente al paciente</li>
              </ol>
            </div>

            <button
              onClick={testConnection}
              disabled={testing}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center justify-center"
            >
              {testing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Probando conexión...
                </>
              ) : (
                'Probar Conexión'
              )}
            </button>

            {testResult && (
              <div className={`p-4 rounded-lg ${testResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
                {testResult.success ? (
                  <p className="text-green-800">
                    <CheckCircle className="w-5 h-5 inline mr-2" />
                    ¡Conexión exitosa!
                  </p>
                ) : (
                  <p className="text-red-800">
                    <AlertCircle className="w-5 h-5 inline mr-2" />
                    Error: {testResult.error || 'No se pudo conectar'}
                  </p>
                )}
              </div>
            )}

            <div className="flex space-x-4">
              <button
                onClick={() => window.location.href = '/dashboard/conversations'}
                className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium"
              >
                Ir a Conversaciones
              </button>
              <button
                onClick={() => window.location.href = '/dashboard/mass-messaging'}
                className="flex-1 py-3 border border-green-600 text-green-600 rounded-lg font-medium hover:bg-green-50"
              >
                Enviar Mensajes Masivos
              </button>
            </div>
          </div>
        )}
      </div>
      </details>
    </div>
    </DashboardLayout>
  );
}
